/**
 * textureImageKitService.js
 * Servizio completo per la gestione delle texture con ImageKit
 * 
 * Questo servizio fornisce funzioni per:
 * - Generare URL ottimizzati per ImageKit
 * - Cache delle texture
 * - Caricamento lazy per Three.js
 * - Precaricamento strategico
 * - Trasformazioni di immagine
 */

import * as THREE from 'three';

// Configurazione base di ImageKit
const IMAGEKIT_ENDPOINT = 'https://ik.imagekit.io';
const IMAGEKIT_PATH = 'materia';
const BASE_URL = `${IMAGEKIT_ENDPOINT}/${IMAGEKIT_PATH}`;

// Dimensioni texture predefinite basate sui device
const TEXTURE_SIZES = {
  small: 512,    // Mobile
  medium: 1024,  // Tablet/Desktop
  large: 2048,   // Desktop HD
  xlarge: 4096   // 4K e oltre
};

// Tipi di mappe texture supportati (usando indici numerici)
const TEXTURE_MAP_TYPES = {
  DIFFUSE: '0',        // Mappa colore/albedo (idtexture_0)
  NORMAL: '1',         // Mappa normali (idtexture_1)
  ROUGHNESS: '2',      // Mappa rugosità (idtexture_2)
  DISPLACEMENT: '3',   // Mappa altezza (idtexture_3)
  AO: '4',             // Ambient Occlusion (idtexture_4)
  METALLIC: '5',       // Mappa metallicità (idtexture_5)
  EMISSIVE: '6',       // Mappa emissiva (idtexture_6)
};

// Per referenza, mapping tra indici e tipo di mappa
const TEXTURE_MAP_INDEX_TO_NAME = {
  '0': 'diffuse',
  '1': 'normal',
  '2': 'roughness',
  '3': 'displacement',
  '4': 'ao',
  '5': 'metallic',
  '6': 'emissive'
};

// Global cache for texture images
const textureCache = {};

/**
 * Genera un URL ImageKit ottimizzato per una texture
 * @param {string} textureId - ID della texture
 * @param {Object} options - Opzioni di trasformazione
 * @returns {string} URL ImageKit
 */
export const getTextureUrl = (textureId, options = {}) => {
  const {
    width = TEXTURE_SIZES.medium,
    quality = 80,
    format = 'auto',
    mapType = TEXTURE_MAP_TYPES.DIFFUSE,
    transformation = ''
  } = options;

  // Costruisci le trasformazioni di base per ImageKit
  // ImageKit usa tr=w-{width},q-{quality} invece della sintassi di Cloudinary
  let transforms = `tr=w-${width},q-${quality}`;
  
  if (format === 'auto') {
    // ImageKit può usare f-auto per formato automatico (WebP dove supportato)
    transforms += ',f-auto';
  } else if (format !== 'original') {
    transforms += `,f-${format}`;
  }
  
  // Aggiungi trasformazioni aggiuntive se presenti
  if (transformation) {
    transforms += `,${transformation}`;
  }

  // Costruisci il nome del file usando il formato numerico (idtexture_0, idtexture_1, ecc.)
  const filename = `${textureId}_${mapType}.jpg`; // Aggiungiamo .jpeg per sicurezza
  
  // Restituisci l'URL completo
  // ImageKit usa la sintassi: endpoint/path/filename?tr=transformazioni
  return `${BASE_URL}/${filename}?${transforms}`;
};

/**
 * Carica una texture con caching
 * @param {string} textureId - ID della texture
 * @param {Object} options - Opzioni di caricamento
 * @returns {Promise<HTMLImageElement>} Promise con l'immagine caricata
 */
export const loadTextureWithCache = (textureId, options = {}) => {
  const url = getTextureUrl(textureId, options);
  const cacheKey = url;
  
  // Verifica se l'immagine è già in cache
  if (textureCache.has(cacheKey)) {
    return Promise.resolve(textureCache.get(cacheKey));
  }
  
  // Altrimenti carica l'immagine
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      textureCache.set(cacheKey, img);
      resolve(img);
    };
    
    img.onerror = (error) => {
      console.error(`Errore nel caricamento della texture ${textureId}:`, error);
      reject(error);
    };
    
    img.src = url;
  });
};

/**
 * Carica una texture per Three.js
 * @param {string} textureId - ID della texture
 * @param {Object} options - Opzioni di caricamento e configurazione
 * @returns {Promise<THREE.Texture>} Promise con la texture Three.js
 */
export const loadThreeTexture = (textureId, options = {}) => {
  const {
    repeat = 1,
    wrapS = THREE.RepeatWrapping,
    wrapT = THREE.RepeatWrapping,
    anisotropy = 4,
    ...urlOptions
  } = options;
  
  const url = getTextureUrl(textureId, urlOptions);
  const cacheKey = `three_${url}`;
  
  // Verifica se la texture Three.js è già in cache
  if (textureCache.has(cacheKey)) {
    return Promise.resolve(textureCache.get(cacheKey));
  }
  
  // Altrimenti carica la texture
  return new Promise((resolve, reject) => {
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    
    loader.load(
      url,
      (texture) => {
        // Configura la texture
        texture.wrapS = wrapS;
        texture.wrapT = wrapT;
        texture.repeat.set(repeat, repeat);
        
        // Imposta l'anisotropia se il renderer la supporta
        if (anisotropy) {
          texture.anisotropy = anisotropy;
        }
        
        textureCache.set(cacheKey, texture);
        resolve(texture);
      },
      undefined, // onProgress
      (error) => {
        console.error(`Errore nel caricamento della texture Three.js ${textureId}:`, error);
        reject(error);
      }
    );
  });
};

/**
 * Carica tutte le mappe texture necessarie per un materiale Three.js
 * @param {string} textureId - ID base della texture
 * @param {THREE.Material} material - Materiale da configurare
 * @param {Object} options - Opzioni di configurazione
 * @returns {Promise<THREE.Material>} Promise con il materiale configurato
 */
export const loadTexturesForMaterial = (textureId, material, options = {}) => {
  const {
    mapTypes = Object.values(TEXTURE_MAP_TYPES),
    size = TEXTURE_SIZES.medium,
    repeat = 1,
    displacementScale = 0.1,
    ...otherOptions
  } = options;
  
  // Mappa tra tipi di texture e proprietà del materiale
  const propertyMap = {
    [TEXTURE_MAP_TYPES.DIFFUSE]: 'map',
    [TEXTURE_MAP_TYPES.NORMAL]: 'normalMap',
    [TEXTURE_MAP_TYPES.ROUGHNESS]: 'roughnessMap',
    [TEXTURE_MAP_TYPES.DISPLACEMENT]: 'displacementMap',
    [TEXTURE_MAP_TYPES.AO]: 'aoMap',
    [TEXTURE_MAP_TYPES.METALLIC]: 'metalnessMap',
    [TEXTURE_MAP_TYPES.EMISSIVE]: 'emissiveMap'
  };
  
  // Filtra i tipi di mappe richiesti
  const requestedMapTypes = mapTypes.filter(type => Object.values(TEXTURE_MAP_TYPES).includes(type));
  
  // Prepara l'array di Promise per tutte le texture
  const texturePromises = requestedMapTypes.map(mapType => {
    const propertyName = propertyMap[mapType];
    
    if (!propertyName) {
      console.warn(`Tipo di mappa sconosciuto: ${mapType}`);
      return Promise.resolve(null);
    }
    
    return loadThreeTexture(textureId, {
      mapType,
      width: size,
      repeat,
      ...otherOptions
    }).then(texture => ({ propertyName, texture, mapType }));
  });
  
  // Attendi che tutte le texture siano caricate
  return Promise.all(texturePromises)
    .then(results => {
      // Applica ogni texture al materiale
      results.forEach(result => {
        if (result && result.texture) {
          material[result.propertyName] = result.texture;
          
          // Imposta la scala di displacement se necessario
          if (result.mapType === TEXTURE_MAP_TYPES.DISPLACEMENT) {
            material.displacementScale = displacementScale;
          }
        }
      });
      
      material.needsUpdate = true;
      return material;
    });
};

/**
 * Verifica quali mappe texture sono disponibili per un ID texture
 * senza necessità di informazioni dal database
 * @param {string} textureId - ID base della texture
 * @param {Object} options - Opzioni aggiuntive
 * @returns {Promise<Object>} Promise con mappa dei tipi di texture disponibili
 */
export const detectAvailableTextureMaps = (textureId, options = {}) => {
  const {
    timeout = 5000, // Timeout in ms per considerare un'immagine non disponibile
    // Tipi di mappa da verificare, default: tutti
    mapTypesToCheck = Object.values(TEXTURE_MAP_TYPES)
  } = options;
  
  // Risultato che conterrà le mappe disponibili (true) o non disponibili (false)
  const result = {};
  
  // Array di promise per il caricamento parallelo
  const checkPromises = mapTypesToCheck.map(mapType => {
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // URL della mappa texture da verificare
      const url = getTextureUrl(textureId, {
        mapType,
        // Richiediamo una versione piccola dell'immagine per velocizzare il check
        width: 32,
        quality: 30
      });
      
      // Timeout per considerare l'immagine non disponibile
      const timeoutId = setTimeout(() => {
        console.log(`Timeout per la mappa ${mapType} della texture ${textureId}`);
        result[mapType] = false;
        resolve();
      }, timeout);
      
      // Se l'immagine carica correttamente, è disponibile
      img.onload = () => {
        clearTimeout(timeoutId);
        result[mapType] = true;
        resolve();
      };
      
      // Se c'è un errore, l'immagine non è disponibile
      img.onerror = () => {
        clearTimeout(timeoutId);
        result[mapType] = false;
        resolve();
      };
      
      // Avvia il caricamento
      img.src = url;
    });
  });
  
  // Attendi che tutti i controlli siano completati
  return Promise.all(checkPromises)
    .then(() => {
      // Filtro solo le mappe disponibili con i loro indici
      const availableMaps = Object.entries(result)
        .filter(([_, isAvailable]) => isAvailable)
        .reduce((acc, [mapType]) => {
          acc.push(mapType);
          return acc;
        }, []);
      
      // Crea un oggetto con informazioni complete
      return {
        textureId,
        allCheckedMaps: result,
        availableMaps,
        // Conversione da indice a nome descrittivo della mappa
        availableMapsDescriptive: availableMaps.map(index => 
          TEXTURE_MAP_INDEX_TO_NAME[index] || `map_${index}`),
        hasMap: (mapType) => result[mapType] === true,
        // Funzione di utility per caricare solo le mappe disponibili
        loadAvailableMaps: (material, materialOptions = {}) => {
          // Filtra per caricare solo le mappe disponibili
          return loadTexturesForMaterial(textureId, material, {
            ...materialOptions,
            mapTypes: availableMaps
          });
        }
      };
    });
};

/**
 * Carica un set di texture con rilevamento automatico delle mappe disponibili
 * @param {string} textureId - ID base della texture
 * @param {THREE.Material} material - Materiale da configurare
 * @param {Object} options - Opzioni di configurazione
 * @returns {Promise<THREE.Material>} Promise con il materiale configurato
 */
export const loadTextureWithAutoDetection = (textureId, material, options = {}) => {
  return detectAvailableTextureMaps(textureId, options)
    .then(result => {
      console.log(`Texture ${textureId}: Rilevate ${result.availableMaps.length} mappe disponibili:`, result.availableMaps);
      
      // Se non ci sono mappe disponibili, restituisci il materiale invariato
      if (result.availableMaps.length === 0) {
        console.warn(`Nessuna mappa texture disponibile per ${textureId}`);
        return material;
      }
      
      // Carica solo le mappe disponibili
      return result.loadAvailableMaps(material, options);
    });
};

/**
 * Precarica un insieme di texture per migliorare le prestazioni
 * @param {Array} textureIds - Array di ID texture da precaricare
 * @param {string} mapType - Tipo di mappa ('0' per diffuse, etc.)
 * @param {Object} options - Opzioni di caricamento
 * @returns {Promise<Object>} - Promise che restituisce un oggetto con gli stati di caricamento
 */
export const preloadTextureImages = (textureIds, mapType = '0', options = {}) => {
  if (!textureIds || !Array.isArray(textureIds) || textureIds.length === 0) {
    return Promise.resolve({ loaded: {}, failed: {} });
  }

  const results = {
    loaded: {},
    failed: {}
  };

  const loadPromises = textureIds.map(id => {
    if (!id) return Promise.resolve();
    
    // Se la texture è già in cache, non caricarla di nuovo
    if (textureCache[id]) {
      results.loaded[id] = textureCache[id];
      return Promise.resolve();
    }
    
    const url = getTextureUrl(id, { ...options, mapType });
    
    return new Promise(resolve => {
      const img = new Image();
      
      img.onload = () => {
        textureCache[id] = url;
        results.loaded[id] = url;
        resolve();
      };
      
      img.onerror = () => {
        console.log(`Failed to load texture: ${id}`);
        results.failed[id] = true;
        resolve();
      };
      
      img.src = url;
    });
  });
  
  return Promise.all(loadPromises).then(() => results);
};

// Helper function to get a cached texture url or null
export const getCachedTextureUrl = (textureId, mapType = '0') => {
  if (!textureId) return null;
  return textureCache[textureId] || null;
};

/**
 * Pulisce la cache delle texture
 * @param {Array<string>} textureIds - Array di ID delle texture da rimuovere (se vuoto, pulisce tutta la cache)
 */
export const clearTextureCache = (textureIds = []) => {
  if (textureIds.length === 0) {
    textureCache.clear();
    console.log('Cache delle texture cancellata completamente');
    return;
  }
  
  textureIds.forEach(id => {
    // Trova e cancella tutte le chiavi che contengono l'ID texture
    const keysToDelete = [];
    textureCache.forEach((_, key) => {
      if (key.includes(id)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      textureCache.delete(key);
    });
  });
  
  console.log(`Cancellate ${textureIds.length} texture dalla cache`);
};

/**
 * Ottiene una texture URL adatta alla risoluzione del dispositivo
 * @param {string} textureId - ID della texture
 * @param {Object} options - Opzioni
 * @returns {string} URL ottimizzato per la risoluzione del dispositivo
 */
export const getResponsiveTextureUrl = (textureId, options = {}) => {
  const { mapType = TEXTURE_MAP_TYPES.DIFFUSE } = options;
  const screenWidth = window.innerWidth;
  let size;
  
  // Determina la dimensione appropriata in base alla larghezza dello schermo
  if (screenWidth <= 768) {
    size = TEXTURE_SIZES.small;
  } else if (screenWidth <= 1280) {
    size = TEXTURE_SIZES.medium;
  } else if (screenWidth <= 1920) {
    size = TEXTURE_SIZES.large;
  } else {
    size = TEXTURE_SIZES.xlarge;
  }
  
  return getTextureUrl(textureId, {
    ...options,
    mapType,
    width: size
  });
};

/**
 * Crea un atlas di texture per WebGL
 * @param {Array<Object>} items - Array di oggetti con proprietà image
 * @param {Object} options - Opzioni di configurazione
 * @returns {Promise<Object>} Promise con il canvas dell'atlas e altre informazioni
 */
export const createTextureAtlas = (items, options = {}) => {
  const {
    cellSize = 512,
    onProgress = null,
    backgroundColor = '#000000'
  } = options;
  
  if (!items || items.length === 0) {
    return Promise.reject(new Error('Nessun elemento fornito per l\'atlas delle texture'));
  }
  
  // Calcola le dimensioni dell'atlas
  const itemCount = Math.max(1, items.length);
  const atlasSize = Math.ceil(Math.sqrt(itemCount));
  
  // Crea il canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = atlasSize * cellSize;
  canvas.height = atlasSize * cellSize;
  
  // Imposta il colore di sfondo
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Carica tutte le immagini
  const imagePromises = items.map((item, index) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        if (onProgress) {
          onProgress(index + 1, items.length);
        }
        resolve(img);
      };
      
      img.onerror = () => {
        console.warn(`Impossibile caricare l'immagine: ${item.image}`);
        
        // Crea un'immagine placeholder
        const placeholderCanvas = document.createElement('canvas');
        placeholderCanvas.width = cellSize;
        placeholderCanvas.height = cellSize;
        const placeholderCtx = placeholderCanvas.getContext('2d');
        
        // Disegna un pattern a scacchiera come placeholder
        placeholderCtx.fillStyle = '#444444';
        placeholderCtx.fillRect(0, 0, cellSize, cellSize);
        placeholderCtx.fillStyle = '#666666';
        const checkerSize = cellSize / 8;
        
        for (let y = 0; y < 8; y++) {
          for (let x = 0; x < 8; x++) {
            if ((x + y) % 2 === 0) {
              placeholderCtx.fillRect(x * checkerSize, y * checkerSize, checkerSize, checkerSize);
            }
          }
        }
        
        const fallbackImg = new Image();
        fallbackImg.src = placeholderCanvas.toDataURL();
        fallbackImg.onload = () => {
          if (onProgress) {
            onProgress(index + 1, items.length);
          }
          resolve(fallbackImg);
        };
      };
      
      img.src = item.image;
    });
  });
  
  // Una volta che tutte le immagini sono caricate, disegnale sull'atlas
  return Promise.all(imagePromises)
    .then(images => {
      images.forEach((img, i) => {
        const x = (i % atlasSize) * cellSize;
        const y = Math.floor(i / atlasSize) * cellSize;
        ctx.drawImage(img, x, y, cellSize, cellSize);
      });
      
      return {
        canvas,
        atlasSize,
        cellSize,
        itemCount,
        // Mappa delle coordinate UV per ogni elemento
        uvCoordinates: items.map((_, i) => {
          const x = (i % atlasSize);
          const y = Math.floor(i / atlasSize);
          
          // Restituisci le coordinate UV normalizzate (0-1)
          return {
            x: x / atlasSize,
            y: y / atlasSize,
            width: 1 / atlasSize,
            height: 1 / atlasSize
          };
        })
      };
    });
};

// Esporta un oggetto con tutte le costanti
export const TextureConstants = {
  SIZES: TEXTURE_SIZES,
  MAP_TYPES: TEXTURE_MAP_TYPES,
  ENDPOINT: IMAGEKIT_ENDPOINT,
  PATH: IMAGEKIT_PATH,
  BASE_URL
};

const textureService = {
  getTextureUrl,
  loadTextureWithCache,
  loadThreeTexture,
  loadTexturesForMaterial,
  preloadTextureImages,
  getCachedTextureUrl,
  clearTextureCache,
  getResponsiveTextureUrl,
  createTextureAtlas,
  detectAvailableTextureMaps,
  loadTextureWithAutoDetection,
  TextureConstants
};

export default textureService; 